import React, { useState, useEffect } from 'react';
import { talentService } from '../../../api/apiService';
import { useAuth } from '../../../contexts/AuthContext';
import '../../../App.css';
import './TalentListSimple.css';

/**
 * Agency & Talent Billing Management
 * - Agencies: Group talents by Agentur, update agency billing info for all
 * - Individual Talents: Talents without agency, update their own VAT/billing
 */
function AgencyManagement() {
  const { currentUser, userRole } = useAuth();
  const [talents, setTalents] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [talentsWithoutAgency, setTalentsWithoutAgency] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection state
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [editMode, setEditMode] = useState(null); // 'agency' or 'talent'

  const [editForm, setEditForm] = useState({});
  const [talentForms, setTalentForms] = useState({}); // Individual talent VAT forms when bill_separately is checked
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Check if user is admin
  const isAdmin = userRole === 'superadmin' || userRole === 'admin' || userRole === 'clientService' || userRole === 'planning';

  // Load talents and group by agency
  useEffect(() => {
    const loadTalents = async () => {
      setLoading(true);
      setError(null);
      try {
        let talentsToProcess = [];

        if (isAdmin) {
          // Admin: load all talents
          const res = await talentService.getTalentsList(1, 5000, '');
          talentsToProcess = res.data.talents || [];
        } else {
          // Talent user: only use their linked talents
          const userTalentIds = currentUser?.talents?.map(t => t.id || t) || [];

          if (userTalentIds.length === 0) {
            setError('No talents linked to your account');
            setLoading(false);
            return;
          }

          // Fetch full talent data for each linked talent
          const talentPromises = userTalentIds.map(id =>
            talentService.getTalentById(id).catch(err => {
              console.error(`Error fetching talent ${id}:`, err);
              return null;
            })
          );
          const results = await Promise.all(talentPromises);
          talentsToProcess = results
            .filter(r => r && r.data)
            .map(r => r.data);
        }

        setTalents(talentsToProcess);

        // Separate talents with and without agency
        const withAgency = [];
        const withoutAgency = [];

        talentsToProcess.forEach(t => {
          const agencyName = t.Agentur;
          if (agencyName && agencyName.trim() !== '') {
            withAgency.push(t);
          } else {
            withoutAgency.push(t);
          }
        });

        // Group talents by agency (Agentur)
        const agencyMap = {};
        withAgency.forEach(t => {
          const agencyName = t.Agentur;
          if (!agencyMap[agencyName]) {
            agencyMap[agencyName] = {
              name: agencyName,
              talents: [],
              // Take agency info from first talent with this agency
              agency_vat_status: t.agency_vat_status || '',
              agency_tax_id: t.agency_tax_id || '',
              agency_vat_id: t.agency_vat_id || '',
              agency_bank_account: t.agency_bank_account || '',
              agency_email: t.agency_email || '',
              AP: t.AP || '',
              is_agency: t.is_agency === 1 || t.is_agency === true,
              agency_bill_separately: t.agency_bill_separately === 1 || t.agency_bill_separately === true,
            };
          }
          agencyMap[agencyName].talents.push(t);
        });

        // Convert to array and sort by name
        const agencyList = Object.values(agencyMap).sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setAgencies(agencyList);
        setTalentsWithoutAgency(withoutAgency);

      } catch (err) {
        console.error('Error loading talents:', err);
        setError(err.message || 'Failed to load talents');
      } finally {
        setLoading(false);
      }
    };
    loadTalents();
  }, [currentUser, userRole, isAdmin]);

  // Select an agency for editing
  const handleSelectAgency = (agency) => {
    setSelectedAgency(agency);
    setSelectedTalent(null);
    setEditMode('agency');
    const firstTalent = agency.talents && agency.talents[0];
    setEditForm({
      Agentur: agency.name || '',
      agency_vat_status: agency.agency_vat_status || '',
      agency_tax_id: agency.agency_tax_id || '',
      agency_vat_id: agency.agency_vat_id || '',
      agency_bank_account: agency.agency_bank_account || '',
      agency_email: agency.agency_email || '',
      AP: agency.AP || '',
      is_agency: agency.is_agency || false,
      agency_bill_separately: agency.agency_bill_separately || false,
      vat_status: firstTalent?.vat_status || '',
      tax_id: firstTalent?.tax_id || '',
      vat_id: firstTalent?.vat_id || '',
      bank_account: firstTalent?.bank_account || '',
      country: firstTalent?.country || '',
      company_name: firstTalent?.company_name || '',
    });
    setSaveMessage('');
  };

  // Select an individual talent for editing
  const handleSelectTalent = (talent) => {
    setSelectedTalent(talent);
    setSelectedAgency(null);
    setEditMode('talent');
    setEditForm({
      Agentur: talent.Agentur || '',
      vat_status: talent.vat_status || '',
      tax_id: talent.tax_id || '',
      vat_id: talent.vat_id || '',
      bank_account: talent.bank_account || '',
      country: talent.country || '',
      company_name: talent.company_name || '',
    });
    setSaveMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Save agency info (applies to all talents under the agency)
  const handleSaveAgency = async (e) => {
    e.preventDefault();
    if (!selectedAgency) return;

    setIsSaving(true);
    setSaveMessage('');

    try {
      const newAgencyName = editForm.Agentur?.trim() || '';
      const oldAgencyName = selectedAgency.name;
      const agencyNameChanged = newAgencyName !== oldAgencyName;

      const updateData = {
        Agentur: newAgencyName,
        agency_vat_status: editForm.agency_vat_status,
        agency_tax_id: editForm.agency_tax_id,
        agency_vat_id: editForm.agency_vat_id,
        agency_bank_account: editForm.agency_bank_account,
        agency_email: editForm.agency_email,
        AP: editForm.AP,
        is_agency: true,
        agency_bill_separately: !!editForm.agency_bill_separately,
      };
      if (editForm.agency_bill_separately) {
        updateData.vat_status = editForm.vat_status;
        updateData.tax_id = editForm.tax_id;
        updateData.vat_id = editForm.vat_id;
        updateData.bank_account = editForm.bank_account;
        updateData.country = editForm.country;
        updateData.company_name = editForm.company_name;
      }

      let successCount = 0;
      let failedCount = 0;

      for (const talent of selectedAgency.talents) {
        try {
          await talentService.updateTalent(talent.id, updateData);
          successCount++;
        } catch (err) {
          console.error(`Error updating talent ${talent.id}:`, err);
          failedCount++;
        }
      }

      // Update local state
      if (agencyNameChanged && newAgencyName === '') {
        // Agency name cleared - move talents to individual talents
        setAgencies(prev => prev.filter(a => a.name !== oldAgencyName));
        setTalentsWithoutAgency(prev => [
          ...prev,
          ...selectedAgency.talents.map(t => ({ ...t, ...updateData }))
        ]);
        setSaveMessage(`Removed agency name. ${successCount} talent(s) moved to Individual Talents.`);
        handleClose();
      } else if (agencyNameChanged) {
        // Agency name changed - update the agency entry
        setAgencies(prev => {
          // Check if new name already exists
          const existingAgency = prev.find(a => a.name === newAgencyName);
          if (existingAgency) {
            // Merge into existing agency
            return prev
              .filter(a => a.name !== oldAgencyName)
              .map(a =>
                a.name === newAgencyName
                  ? {
                      ...a,
                      ...updateData,
                      name: newAgencyName,
                      talents: [...a.talents, ...selectedAgency.talents.map(t => ({ ...t, ...updateData }))]
                    }
                  : a
              );
          } else {
            // Rename the agency
            return prev.map(a =>
              a.name === oldAgencyName
                ? { ...a, ...updateData, name: newAgencyName, talents: selectedAgency.talents.map(t => ({ ...t, ...updateData })) }
                : a
            ).sort((a, b) => a.name.localeCompare(b.name));
          }
        });
        setSelectedAgency(prev => ({ ...prev, ...updateData, name: newAgencyName }));
        if (failedCount === 0) {
          setSaveMessage(`Agency renamed to "${newAgencyName}". Updated ${successCount} talent(s).`);
        } else {
          setSaveMessage(`Agency renamed. Updated ${successCount} talent(s), ${failedCount} failed.`);
        }
      } else {
        // No name change - just update other fields
        setAgencies(prev => prev.map(a =>
          a.name === selectedAgency.name
            ? { ...a, ...updateData }
            : a
        ));
        setSelectedAgency(prev => ({ ...prev, ...updateData }));
        if (failedCount === 0) {
          setSaveMessage(`Updated ${successCount} talent(s) successfully!`);
        } else {
          setSaveMessage(`Updated ${successCount} talent(s), ${failedCount} failed.`);
        }
      }
    } catch (err) {
      console.error('Error saving agency info:', err);
      setSaveMessage('Error saving: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Save individual talent billing info
  const handleSaveTalent = async (e) => {
    e.preventDefault();
    if (!selectedTalent) return;

    setIsSaving(true);
    setSaveMessage('');

    try {
      const updateData = {
        Agentur: editForm.Agentur,
        vat_status: editForm.vat_status,
        tax_id: editForm.tax_id,
        vat_id: editForm.vat_id,
        bank_account: editForm.bank_account,
        country: editForm.country,
        company_name: editForm.company_name,
      };

      await talentService.updateTalent(selectedTalent.id, updateData);

      // Update local state - if agency name was added, move to agencies list
      if (editForm.Agentur && editForm.Agentur.trim() !== '') {
        // Remove from individual talents
        setTalentsWithoutAgency(prev => prev.filter(t => t.id !== selectedTalent.id));

        // Add to agencies or create new agency group
        setAgencies(prev => {
          const existingAgency = prev.find(a => a.name === editForm.Agentur);
          if (existingAgency) {
            // Add to existing agency
            return prev.map(a =>
              a.name === editForm.Agentur
                ? { ...a, talents: [...a.talents, { ...selectedTalent, ...updateData }] }
                : a
            );
          } else {
            // Create new agency
            return [...prev, {
              name: editForm.Agentur,
              talents: [{ ...selectedTalent, ...updateData }],
              agency_vat_status: '',
              agency_tax_id: '',
              agency_vat_id: '',
              agency_bank_account: '',
              agency_email: '',
              AP: '',
            }].sort((a, b) => a.name.localeCompare(b.name));
          }
        });

        setSaveMessage('Talent updated and moved to agency: ' + editForm.Agentur);
        handleClose();
      } else {
        setTalentsWithoutAgency(prev => prev.map(t =>
          t.id === selectedTalent.id
            ? { ...t, ...updateData }
            : t
        ));
        setSelectedTalent(prev => ({ ...prev, ...updateData }));
        setSaveMessage('Talent billing info updated successfully!');
      }
    } catch (err) {
      console.error('Error saving talent info:', err);
      setSaveMessage('Error saving: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedAgency(null);
    setSelectedTalent(null);
    setEditMode(null);
    setEditForm({});
    setSaveMessage('');
  };

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1>Billing Management</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Billing Management</h1>
        <p className="talent-list-simple-intro">
          Manage billing info for agencies and individual talents.
        </p>

        {error && (
          <div className="talent-list-simple-message error">
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '30px' }}>
          {/* Left Panel - Lists */}
          <div style={{ flex: '1', maxWidth: '400px' }}>
            {/* Agencies Section */}
            <h2>Agencies ({agencies.length})</h2>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              Talents grouped by agency. Updates apply to all talents.
            </p>
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '5px',
              marginBottom: '20px'
            }}>
              {agencies.length === 0 ? (
                <p style={{ padding: '15px', textAlign: 'center', color: '#666' }}>
                  No agencies found
                </p>
              ) : (
                agencies.map((agency, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectAgency(agency)}
                    style={{
                      padding: '10px 15px',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      backgroundColor: selectedAgency?.name === agency.name ? '#e3f2fd' : 'white',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedAgency?.name !== agency.name) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedAgency?.name !== agency.name) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <div style={{ fontWeight: '500' }}>{agency.name}</div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                      {agency.talents.length} talent(s)
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Individual Talents Section */}
            <h2>Individual Talents ({talentsWithoutAgency.length})</h2>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              Talents without an agency. Update their own billing info.
            </p>
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '5px'
            }}>
              {talentsWithoutAgency.length === 0 ? (
                <p style={{ padding: '15px', textAlign: 'center', color: '#666' }}>
                  No individual talents found
                </p>
              ) : (
                talentsWithoutAgency.map((talent, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectTalent(talent)}
                    style={{
                      padding: '10px 15px',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      backgroundColor: selectedTalent?.id === talent.id ? '#fff3e0' : 'white',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedTalent?.id !== talent.id) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedTalent?.id !== talent.id) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <div style={{ fontWeight: '500' }}>{talent.Name || talent.name}</div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                      {talent.E_Mail || talent.email || 'No email'}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Edit Form */}
          <div style={{ flex: '2' }}>
            {editMode === 'agency' && selectedAgency ? (
              <>
                <h2>Agency: {selectedAgency.name}</h2>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                  Updating will apply to {selectedAgency.talents.length} talent(s):
                  <span style={{ fontSize: '12px', display: 'block', marginTop: '5px' }}>
                    {selectedAgency.talents.map(t => t.Name || t.name).join(', ')}
                  </span>
                </p>

                {saveMessage && (
                  <div style={{
                    padding: '10px',
                    marginBottom: '15px',
                    borderRadius: '5px',
                    backgroundColor: saveMessage.includes('Error') || saveMessage.includes('failed') ? '#ffebee' : '#e8f5e9',
                    color: saveMessage.includes('Error') || saveMessage.includes('failed') ? '#c62828' : '#2e7d32'
                  }}>
                    {saveMessage}
                  </div>
                )}

                <form onSubmit={handleSaveAgency}>
                  <div className="form-group">
                    <label>Agency Name</label>
                    <input
                      type="text"
                      name="Agentur"
                      value={editForm.Agentur || ''}
                      onChange={handleInputChange}
                      placeholder="Agency name"
                    />
                    <small style={{ color: '#666', fontSize: '11px' }}>
                      Changing the name will update all talents under this agency.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>AP (Agency Percentage)</label>
                    <input
                      type="text"
                      name="AP"
                      value={editForm.AP || ''}
                      onChange={handleInputChange}
                      placeholder="e.g. 15"
                    />
                  </div>

                  <div className="form-group">
                    <label>Agency VAT Status</label>
                    <select
                      name="agency_vat_status"
                      value={editForm.agency_vat_status || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">— Select —</option>
                      <option value="with_vat">With VAT (19%)</option>
                      <option value="without_vat">Without VAT</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Agency Tax ID</label>
                    <input
                      type="text"
                      name="agency_tax_id"
                      value={editForm.agency_tax_id || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Agency VAT ID</label>
                    <input
                      type="text"
                      name="agency_vat_id"
                      value={editForm.agency_vat_id || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Agency Bank Account</label>
                    <textarea
                      name="agency_bank_account"
                      value={editForm.agency_bank_account || ''}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Agency Email</label>
                    <input
                      type="email"
                      name="agency_email"
                      value={editForm.agency_email || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group talent-edit-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="agency_bill_separately"
                        checked={!!editForm.agency_bill_separately}
                        onChange={handleInputChange}
                      />
                      {' '}Bill separately (talent CN + agency provision CN)
                    </label>
                    <small className="talent-edit-hint" style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '12px' }}>
                      When checked: first credit note = talent with talent VAT; second = agency provision (AP %) with agency VAT. When unchecked: one credit note to agency with all lines and agency VAT.
                    </small>
                  </div>

                  {editForm.agency_bill_separately && (
                    <>
                      <h3 className="talent-edit-section-title" style={{ marginTop: '20px', marginBottom: '10px' }}>Talent VAT / Billing (apply to all {selectedAgency.talents.length} talent(s))</h3>
                      <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                        Update each talent&apos;s own VAT and billing. These values are used for the talent credit note when billing separately.
                      </p>
                      <div className="form-group">
                        <label>VAT Status</label>
                        <select
                          name="vat_status"
                          value={editForm.vat_status || ''}
                          onChange={handleInputChange}
                        >
                          <option value="">— Select —</option>
                          <option value="with_vat">With VAT (19%)</option>
                          <option value="without_vat">Without VAT</option>
                          <option value="kleinunternehmer">Kleinunternehmer</option>
                          <option value="auslander">Ausländer (abroad)</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Tax ID</label>
                        <input
                          type="text"
                          name="tax_id"
                          value={editForm.tax_id || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-group">
                        <label>VAT ID</label>
                        <input
                          type="text"
                          name="vat_id"
                          value={editForm.vat_id || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-group">
                        <label>Bank Account</label>
                        <textarea
                          name="bank_account"
                          value={editForm.bank_account || ''}
                          onChange={handleInputChange}
                          rows={3}
                        />
                      </div>
                      <div className="form-group">
                        <label>Country</label>
                        <input
                          type="text"
                          name="country"
                          value={editForm.country || ''}
                          onChange={handleInputChange}
                          placeholder="e.g. Germany"
                        />
                      </div>
                      <div className="form-group">
                        <label>Company Name</label>
                        <input
                          type="text"
                          name="company_name"
                          value={editForm.company_name || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                    </>
                  )}

                  <div className="button-group" style={{ marginTop: '20px' }}>
                    <button type="submit" className="button primary" disabled={isSaving}>
                      {isSaving ? `Saving to ${selectedAgency.talents.length} talent(s)...` : 'Save to All Talents'}
                    </button>
                    <button type="button" className="button secondary" onClick={handleClose} disabled={isSaving}>
                      Close
                    </button>
                  </div>
                </form>
              </>
            ) : editMode === 'talent' && selectedTalent ? (
              <>
                <h2>Talent: {selectedTalent.Name || selectedTalent.name}</h2>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                  {selectedTalent.E_Mail || selectedTalent.email}
                </p>

                {saveMessage && (
                  <div style={{
                    padding: '10px',
                    marginBottom: '15px',
                    borderRadius: '5px',
                    backgroundColor: saveMessage.includes('Error') ? '#ffebee' : '#e8f5e9',
                    color: saveMessage.includes('Error') ? '#c62828' : '#2e7d32'
                  }}>
                    {saveMessage}
                  </div>
                )}

                <form onSubmit={handleSaveTalent}>
                  <div className="form-group">
                    <label>Agency Name (Agentur)</label>
                    <input
                      type="text"
                      name="Agentur"
                      value={editForm.Agentur || ''}
                      onChange={handleInputChange}
                      placeholder="Enter agency name if applicable"
                    />
                    <small style={{ color: '#666', fontSize: '11px' }}>
                      If you add an agency name, this talent will be grouped under that agency.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>VAT Status</label>
                    <select
                      name="vat_status"
                      value={editForm.vat_status || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">— Select —</option>
                      <option value="with_vat">With VAT (19%)</option>
                      <option value="without_vat">Without VAT</option>
                      <option value="kleinunternehmer">Kleinunternehmer</option>
                      <option value="auslander">Ausländer (abroad)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Tax ID</label>
                    <input
                      type="text"
                      name="tax_id"
                      value={editForm.tax_id || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>VAT ID</label>
                    <input
                      type="text"
                      name="vat_id"
                      value={editForm.vat_id || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Bank Account</label>
                    <textarea
                      name="bank_account"
                      value={editForm.bank_account || ''}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      name="country"
                      value={editForm.country || ''}
                      onChange={handleInputChange}
                      placeholder="e.g. Germany"
                    />
                  </div>

                  <div className="form-group">
                    <label>Company Name</label>
                    <input
                      type="text"
                      name="company_name"
                      value={editForm.company_name || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="button-group" style={{ marginTop: '20px' }}>
                    <button type="submit" className="button primary" disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" className="button secondary" onClick={handleClose} disabled={isSaving}>
                      Close
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#666',
                backgroundColor: '#f9f9f9',
                borderRadius: '5px'
              }}>
                <p>Select an agency or talent from the list to edit billing info.</p>
                <p style={{ fontSize: '12px', marginTop: '10px' }}>
                  <strong>Agencies:</strong> Changes apply to all talents under the agency.<br />
                  <strong>Individual Talents:</strong> Changes apply only to that talent.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgencyManagement;
